
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useParallax } from '../../../hooks/useParallax';
import { useMagneticButton } from '../../../hooks/useMagneticButton';

export default function CTA() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  const parallax1 = useParallax({ speed: 0.2, direction: 'up' });
  const parallax2 = useParallax({ speed: 0.3, direction: 'down' });
  const magneticButton1 = useMagneticButton({ strength: 0.3, tolerance: 100 });

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const certificates = [
    { 
      icon: 'ri-shield-check-line', 
      title: 'LGPD', 
      subtitle: 'Conformidade Total',
      color: 'from-emerald-500 to-teal-500'
    },
    { 
      icon: 'ri-lock-2-line', 
      title: 'SSL', 
      subtitle: 'Segurança Certificada',
      color: 'from-teal-500 to-cyan-500'
    },
    { 
      icon: 'ri-scales-3-line', 
      title: 'Portaria 671', 
      subtitle: 'Ministério do Trabalho',
      color: 'from-cyan-500 to-blue-500'
    },
    { 
      icon: 'ri-cloud-line', 
      title: 'Cloud', 
      subtitle: 'Infraestrutura Segura',
      color: 'from-blue-500 to-indigo-500'
    }
  ];

  return (
    <>
      <section 
        ref={ref}
        className={`py-20 lg:py-28 bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 relative overflow-hidden transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
        }`}
      >
        {/* Padrão de fundo sutil */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Elementos decorativos com parallax */}
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute top-20 left-10 w-64 h-64 bg-teal-400 rounded-full blur-3xl"
            style={{ transform: `translateY(${parallax1}px)` }}
          ></div>
          <div 
            className="absolute bottom-20 right-10 w-80 h-80 bg-cyan-400 rounded-full blur-3xl"
            style={{ transform: `translateY(${parallax2}px)` }}
          ></div>
        </div>

        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          {/* Logo da Empresa */}
          <div className={`mb-12 flex justify-center transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          }`}>
            <div className="group relative cursor-pointer">
              <div className="absolute -inset-4 bg-white/20 rounded-3xl blur-xl group-hover:bg-white/30 transition-all duration-300"></div>
              <div className="relative bg-white/10 backdrop-blur-sm p-4 sm:p-6 lg:p-8 rounded-2xl border-2 border-white/30 group-hover:border-white/50 transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/2fb69e05e600ee45b7cfaa2e2ddcddf8/b782f25abe3d9999e649c275d9ff9c00.png"
                  alt="Logo Controle de Ponto Rural"
                  className="h-24 sm:h-32 lg:h-40 w-auto max-w-full object-contain group-hover:scale-110 transition-transform duration-300"
                />
              </div>
            </div>
          </div>

          <h2 className={`text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            Pronto para ter controle real sobre sua equipe?
          </h2>
          <p className={`text-lg md:text-xl text-white/90 mb-10 leading-relaxed max-w-2xl mx-auto transition-all duration-700 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            Junte-se a centenas de produtores que já transformaram a gestão de suas equipes
          </p>

          <div className={`transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <div className="flex justify-center">
              <button
                ref={magneticButton1.ref as React.RefObject<HTMLButtonElement>}
                onClick={() => scrollToSection('contato')}
                className="group relative px-10 py-5 bg-white text-teal-700 font-bold rounded-full hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-2xl overflow-hidden"
                style={{
                  transform: `translate(${magneticButton1.position.x}px, ${magneticButton1.position.y}px) scale(${magneticButton1.isHovering ? 1.1 : 1})`
                }}
              >
                <span className="mr-3">Começar Agora</span>
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-2 transition-transform"></i>
                
                {/* Efeito magnético visual */}
                {magneticButton1.isHovering && (
                  <>
                    <div className="absolute inset-0 border-2 border-teal-400/50 rounded-full animate-pulse"></div>
                    <div className="absolute -inset-1 bg-gradient-to-r from-teal-400 to-cyan-400 rounded-full blur opacity-30 animate-pulse"></div>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Certificados e Conformidades */}
          <div className={`mt-16 pt-12 border-t border-white/20 transition-all duration-700 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <p className="text-white/80 text-sm font-semibold mb-8 uppercase tracking-wider">
              Certificações e Conformidades
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {certificates.map((cert, index) => (
                <div
                  key={index}
                  className={`group relative cursor-pointer transition-all duration-500 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: `${700 + index * 100}ms` }}
                >
                  <div className="absolute -inset-2 bg-white/10 rounded-2xl blur-lg group-hover:bg-white/20 transition-all duration-300"></div>
                  <div className="relative bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 group-hover:border-white/40 transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl">
                    <div className={`w-14 h-14 mx-auto mb-3 bg-gradient-to-br ${cert.color} rounded-xl flex items-center justify-center shadow-lg group-hover:rotate-12 transition-all duration-300`}>
                      <i className={`${cert.icon} text-2xl text-white group-hover:scale-125 transition-transform`}></i>
                    </div>
                    <h3 className="text-white font-bold text-base mb-1 group-hover:text-cyan-200 transition-colors">
                      {cert.title}
                    </h3>
                    <p className="text-white/70 text-xs group-hover:text-white/90 transition-colors">
                      {cert.subtitle}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Badges de Confiança */}
          <div className={`mt-12 flex flex-wrap justify-center items-center gap-6 transition-all duration-700 delay-1000 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          }`}>
            <div className="group flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-5 py-3 rounded-full border border-white/20 hover:border-white/40 hover:bg-white/15 transition-all duration-300 cursor-pointer">
              <i className="ri-shield-check-line text-xl text-white group-hover:scale-125 transition-transform"></i>
              <span className="text-white text-sm font-medium whitespace-nowrap">100% Seguro</span>
            </div>
            <div className="group flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-5 py-3 rounded-full border border-white/20 hover:border-white/40 hover:bg-white/15 transition-all duration-300 cursor-pointer">
              <i className="ri-customer-service-2-line text-xl text-white group-hover:scale-125 transition-transform"></i>
              <span className="text-white text-sm font-medium whitespace-nowrap">Suporte 24/7</span>
            </div>
            <div className="group flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-5 py-3 rounded-full border border-white/20 hover:border-white/40 hover:bg-white/15 transition-all duration-300 cursor-pointer">
              <i className="ri-refresh-line text-xl text-white group-hover:scale-125 transition-transform"></i>
              <span className="text-white text-sm font-medium whitespace-nowrap">Garantia de satisfação</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
